import {
  UseFormRegister,
  RegisterOptions,
  FieldError,
  UseFormClearErrors,
  UseFormSetValue,
} from "react-hook-form";
import { useState, useEffect, useRef } from "react";
import { IconChevronDown, IconSearch } from "@tabler/icons-react";
import { useLanguage } from "../context/LanguageContext";

type SelectOption = {
  id?: string;
  value: string;
  text: string;
  type?: string; // Added type field
};

type SelectedValue = {
  id?: string;
  value: string;
  text: string;
  type?: string;
};

type Props = {
  name: string;
  label?: string;
  placeholder?: string;
  selectOptions: SelectOption[];
  register?: UseFormRegister<any>;
  validation?: RegisterOptions;
  error?: FieldError | null;
  className?: string;
  labelClass?: string;
  disabled?: boolean;
  removeDiv?: boolean;
  value?: SelectedValue[];
  setValue?: (val: SelectedValue[]) => void;
  onChange?: (val: SelectedValue[]) => void;
  clearErrors?: UseFormClearErrors<any>;
  formSetValue?: UseFormSetValue<any>;
  multiple?: boolean;
  other?: any;
};

export default function NewSelectInput({
  name,
  label,
  placeholder = "Select",
  selectOptions,
  register,
  validation,
  error,
  className = "",
  labelClass = "",
  disabled = false,
  removeDiv = false,
  value = [],
  setValue,
  onChange,
  clearErrors,
  formSetValue,
  multiple = true,
  other,
}: Props) {
  const { t } = useLanguage();
  const [selectOpen, setSelectOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredOptions = selectOptions?.filter((opt) =>
    opt?.text?.toLowerCase()?.includes(searchText?.toLowerCase())
  );

  const handleSelect = (selected: SelectOption) => {
    let newValue: SelectedValue[];
    if (multiple) {
      const exists = value.find((v) => v.value === selected.value);
      newValue = exists
        ? value.filter((v) => v.value !== selected.value)
        : [
            ...value,
            {
              id: selected.id,
              value: selected.value,
              text: selected.text,
              type: selected.type,
            },
          ];
    } else {
      newValue = [
        {
          id: selected.id,
          value: selected.value,
          text: selected.text,
          type: selected.type,
        },
      ];
      setSelectOpen(false);
    }

    setValue && setValue(newValue);
    onChange && onChange(newValue);
    clearErrors && clearErrors(name);
    formSetValue && formSetValue(name, newValue);
  };

  const selectedText = multiple
    ? value?.map((v) => t(v.text)).join(", ")
    : value[0]?.text ? t(value[0].text) : "";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setSelectOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDropdownToggle = () => {
    if (disabled) return;

    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setOpenUpward(spaceBelow < 250 && spaceAbove > 250);
    }

    setSelectOpen((prev) => !prev);
  };

  const labelNode = label && (
    <label
      htmlFor={name}
      className={`text-[#1E293B] text-sm ${labelClass} whitespace-nowrap`}
    >
      {t(label)}
      {validation?.required && <span className="text-red-600 ml-1">*</span>}
    </label>
  );

  const dropdown = (
    <div
      className={`absolute z-30 left-0 w-full rounded-xl bg-white border border-gray-200 shadow-lg max-h-60 overflow-y-auto ${
        openUpward ? "bottom-full mb-1" : "top-full mt-1"
      }`}
    >
      <div className="p-2">
        <div className="flex items-center gap-2 px-2 py-1 bg-gray-100 rounded-md">
          <IconSearch size={16} className="text-gray-500" />
          <input
            type="text"
            className="w-full bg-transparent text-sm outline-none placeholder:text-gray-400"
            placeholder={t("Search...")}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
      </div>
      <ul>
        {multiple && filteredOptions?.length > 0 && (
          <li
            className={`px-4 py-2 text-sm hover:bg-blue-100 cursor-pointer flex items-center gap-2 ${
              value.length === selectOptions.length ? "bg-blue-50 font-medium" : ""
            }`}
            onClick={() => {
              if (value.length === selectOptions.length) {
                setValue && setValue([]);
                onChange && onChange([]);
                clearErrors && clearErrors(name);
                formSetValue && formSetValue(name, []);
              } else {
                const allSelected = selectOptions.map(opt => ({
                  id: opt.id,
                  value: opt.value,
                  text: opt.text,
                  type: opt.type,
                }));
                setValue && setValue(allSelected);
                onChange && onChange(allSelected);
                clearErrors && clearErrors(name);
                formSetValue && formSetValue(name, allSelected);
              }
            }}
          >
            <label className="relative flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={value.length === selectOptions.length}
                readOnly
                className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] 
                              bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
              />
              <span
                className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 
                              top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
              >
                ✓
              </span>
            </label>
            <span>Select All</span>
          </li>
        )}
        {filteredOptions?.map((opt, i) => (
          <li
            key={opt.id ?? i}
            className={`px-4 py-2 text-sm hover:bg-blue-100 cursor-pointer flex items-center gap-2 ${
              value.some(
                (v) =>
                  v.value === opt.value &&
                  (opt.type === undefined || v.type === opt.type)
              )
                ? "bg-blue-50 font-medium"
                : ""
            }`}
            onClick={() => handleSelect(opt)}
          >
            {multiple && (
              <label className="relative flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={value.some(
                    (v) =>
                      v.value === opt.value &&
                      (opt.type === undefined || v.type === opt.type)
                  )}
                  readOnly
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] 
                                bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                />
                <span
                  className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 
                                top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                >
                  ✓
                </span>
              </label>

              //   <input
              //     type="checkbox"
              //     checked={value.some((v) => v.value === opt.value)}
              //     readOnly
              //     className="form-checkbox"
              //   />
            )}
            <span>{t(opt.text)}</span>
          </li>
        ))}
        {filteredOptions?.length === 0 && (
          <li className="px-4 py-2 text-sm text-gray-400">{t("No results found")}</li>
        )}
      </ul>
    </div>
  );

  const selectField = (
    <div ref={dropdownRef} className="relative w-full">
      <div
        className={`flex items-center justify-between px-4 py-2 text-sm rounded-xl border ${
          error ? "border-red-500" : "border-gray-200"
        } bg-white cursor-pointer ${disabled ? "opacity-50" : ""}`}
        onClick={handleDropdownToggle}
      >
        <span className="truncate text-[#1E1E1E]">
          {selectedText ? t(selectedText) : t(placeholder)}
        </span>
        <IconChevronDown
          size={18}
          stroke={2.2}
          className={`text-gray-500 transition-transform ${
            selectOpen ? "rotate-180" : ""
          }`}
        />
      </div>

      {selectOpen && dropdown}

      {register && !formSetValue && (
        <input
          type="hidden"
          {...register(name, validation)}
          value={value?.map((v) => v.value).join(",") || ""}
        />
      )}
    </div>
  );

  return (
    <div className={`space-y-1 relative ${className}`}>
      {labelNode}
      <div className="w-full">
        {removeDiv ? (
          selectField
        ) : (
          <div className="relative">{selectField}</div>
        )}
        <span className="text-red-500 text-sm block pl-3">
          {error?.message || ""}
        </span>
      </div>
    </div>
  );
}
